import type { ConsentState } from "./consent.js";

/**
 * The consent choice as a first-party cookie on the registrable domain.
 *
 * localStorage is per-origin, so a banner choice on the marketing site never
 * reached app.customer.com: the subdomain stayed on the configured default
 * (usually denied) forever, and every event from it was parked. Found live on
 * app.rechtswijs.nl, 31 August 2026, after a week of zero events from a
 * perfectly healthy script. The cookie carries the SAME choice across every
 * subdomain of the site.
 *
 * The cookie stores only the choice itself ("g:d"), no identifier, and it is
 * deliberately written for a DENIED choice too: remembering a refusal is what
 * keeps a banner from re-asking, and is the privacy-friendly direction.
 */

export const CONSENT_COOKIE_NAME = "_tst_consent";
/** Fresh CMP reading for WooCommerce's later server-side paid-order callback. */
export const CMP_CONSENT_COOKIE_NAME = "_tst_cmp_consent";
export const CMP_CONSENT_MAX_AGE_SECONDS = 10 * 60;

/** How long a consent choice is remembered; the common 12-month practice. */
export const CONSENT_COOKIE_MAX_AGE_DAYS = 365;

const CHAR_OF: Record<ConsentState["analytics"], string> = {
  granted: "g",
  denied: "d",
  not_set: "n",
};

function stateOf(char: string): ConsentState["analytics"] | null {
  if (char === "g") return "granted";
  if (char === "d") return "denied";
  if (char === "n") return "not_set";
  return null;
}

/** "granted/denied" pair to the compact cookie value ("g:d"). */
export function encodeConsentCookie(state: ConsentState): string {
  return `${CHAR_OF[state.analytics]}:${CHAR_OF[state.marketing]}`;
}

export function cmpConsentCookieValue(state: ConsentState, domain: string | null, secure = false): string {
  return `${CMP_CONSENT_COOKIE_NAME}=${encodeConsentCookie(state)}; path=/; max-age=${CMP_CONSENT_MAX_AGE_SECONDS}; SameSite=Lax`
    + (secure ? "; Secure" : "")
    + (domain ? `; domain=${domain}` : "");
}

/** The inverse; null for anything that is not a valid value. */
export function decodeConsentCookie(value: string): ConsentState | null {
  const parts = value.split(":");
  if (parts.length !== 2) return null;
  const analytics = stateOf(parts[0]!);
  const marketing = stateOf(parts[1]!);
  if (analytics === null || marketing === null) return null;
  return { analytics, marketing };
}

/** Pull the consent state out of a document.cookie string, or null. */
export function consentFromCookieString(cookieString: string): ConsentState | null {
  const parts = cookieString.split(`${CONSENT_COOKIE_NAME}=`);
  if (parts.length < 2) return null;
  const value = parts[1]!.split(";")[0]!.trim();
  return decodeConsentCookie(value);
}
